import { useSyncExternalStore } from "react";

/** One message in the toast bar. Every `showToast()` call gets a new id, so repeating a message restarts its timer. */
export interface Toast {
	readonly id: number;
	readonly message: string;
}

/** How long a toast stays while nobody points at it or focuses it; the ravnar.io/demo toast bar uses the same default. */
export const TOAST_DURATION_MS = 7000;

// Module state lives in an object so functions update a property instead of reassigning a module-level binding.
const store: { toast: Toast | undefined; lastId: number; readonly listeners: Set<() => void> } = {
	toast: undefined,
	lastId: 0,
	listeners: new Set(),
};

function setToast(toast: Toast | undefined): void {
	store.toast = toast;
	for (const listener of store.listeners) {
		listener();
	}
}

function subscribe(listener: () => void): () => void {
	store.listeners.add(listener);
	return () => {
		store.listeners.delete(listener);
	};
}

function getToast(): Toast | undefined {
	return store.toast;
}

/**
 * Confirms that an action worked, in the toast bar at the foot of the screen, and replaces any toast already showing.
 * Call it once the request has succeeded. Errors do not go here: they stay next to their form in a `role="alert"`
 * paragraph, because they last until they are fixed and a toast cannot show over an open Drawer.
 */
export function showToast(message: string): void {
	store.lastId += 1;
	setToast({ id: store.lastId, message });
}

/** Hides the toast with this id. Does nothing when another toast has replaced it in the meantime. */
export function dismissToast(id: number): void {
	if (store.toast?.id === id) {
		setToast(undefined);
	}
}

/** Clears the toast bar; src/test/setup.ts calls it after each test so a toast never leaks into the next one. */
export function resetToasts(): void {
	setToast(undefined);
}

/** The toast showing now, if any. */
export function useToast(): Toast | undefined {
	return useSyncExternalStore(subscribe, getToast);
}
